import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    model,
    OnInit,
    output
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ApplicationStatusEnum, AwardCommitteeMeeting } from '@tran-monorepo/interfaces/applications';
import { filter, tap } from 'rxjs';
import { formatAwardCommitteeMeetingDate } from '../../../utils';
import { ApplicationStatusIndicatorComponent } from '../../applications';
import { CheckboxComponent } from '../../form/checkbox/checkbox.component';
import { ApplicationDetailDashboardBoxComponent } from '../application-detail-dashboard-box/application-detail-dashboard-box.component';
import { ApplicationStatusForm } from '../application-detail.form';
import { ApplicationStatusSelectComponent } from './application-status-select/application-status-select.componen';
import { AwardCommitteeMeetingSelectComponent } from './award-committee-meeting-select/award-committe-meeting-select.component';

@Component({
    selector: 'tran-application-status-detail',
    imports: [
        ReactiveFormsModule,
        ApplicationDetailDashboardBoxComponent,
        ApplicationStatusIndicatorComponent,
        TranslateModule,
        CheckboxComponent,
        ApplicationStatusSelectComponent,
        AwardCommitteeMeetingSelectComponent
    ],
    templateUrl: './application-status-detail.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplicationStatusDetailComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);

    public readonly heading = input<string>(
        'components.application-detail-dashboard.application-status-detail.heading'
    );
    public readonly readonly = input(false, { transform: booleanAttribute });
    public readonly isLoading = model<boolean>(false);
    public readonly form = input<ApplicationStatusForm | null>(null);
    public readonly isMailCommunicationDisabled = input<boolean>(false);
    public readonly status = input.required<ApplicationStatusEnum>();
    public readonly isSelectedAwardCommitteeLoading = input.required<boolean>();
    public readonly selectedAwardCommitteeMeeting = input.required<AwardCommitteeMeeting | null>();

    public readonly isAwardCommitteeMeetingsLoading = input<boolean>(false);
    public readonly hasAwardCommitteeMeetingsNextPage = input<boolean>(false);
    public readonly awardCommitteeMeetings = input<AwardCommitteeMeeting[]>([]);

    public readonly loadMoreAwardCommitteeMeetings = output<void>();

    protected readonly effectiveReadonly = computed(
        () =>
            this.readonly() ||
            this.status() === ApplicationStatusEnum.Draft ||
            this.status() === ApplicationStatusEnum.Approved ||
            this.status() === ApplicationStatusEnum.Denied
    );
    protected readonly showMailCommunicationSetting = computed(() => this.status() !== ApplicationStatusEnum.Draft);
    protected readonly formattedAwardCommitteeMeeting = computed(() => {
        const selectedAwardCommitteeMeeting = this.selectedAwardCommitteeMeeting();
        if (!selectedAwardCommitteeMeeting) {
            return '';
        }
        return formatAwardCommitteeMeetingDate(selectedAwardCommitteeMeeting);
    });
    protected readonly isAwardCommitteeMeetingLoading = computed(
        () => this.isLoading() || this.isSelectedAwardCommitteeLoading()
    );

    private readonly statusWithDisabledAwardCommitteeSelect = [
        ApplicationStatusEnum.Draft,
        ApplicationStatusEnum.Submitted,
        ApplicationStatusEnum.Approved,
        ApplicationStatusEnum.Denied
    ];

    private lastHandledStatus: ApplicationStatusEnum | null = null;

    get awardCommitteeMeetingIdControl(): FormControl<string | null> {
        return this.form()?.controls?.awardCommitteeMeetingId as FormControl<string | null>;
    }

    get statusControl(): FormControl<ApplicationStatusEnum | null> {
        return this.form()?.controls?.status as FormControl<ApplicationStatusEnum | null>;
    }

    constructor() {
        effect(() => {
            if (!this.readonly()) {
                this.patchValues(this.form());
            }
        });
    }

    public ngOnInit(): void {
        this.initValueChangeSubscription();
    }

    private initValueChangeSubscription(): void {
        this.statusControl?.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(status => !!status),
                tap(status => this.handleStatusChange(status))
            )
            .subscribe();
    }

    private handleStatusChange(status: ApplicationStatusEnum): void {
        // User selection and the inbound `status()` sync both emit `valueChanges` for the same status.
        if (this.lastHandledStatus === status) {
            return;
        }
        this.lastHandledStatus = status;

        if (this.statusWithDisabledAwardCommitteeSelect.includes(status)) {
            this.awardCommitteeMeetingIdControl.disable({ emitEvent: false });
        } else {
            this.awardCommitteeMeetingIdControl.enable({ emitEvent: false });
            if (!this.awardCommitteeMeetings()?.length && !this.isAwardCommitteeMeetingsLoading()) {
                this.loadMoreAwardCommitteeMeetings.emit();
            }
            if (status === ApplicationStatusEnum.AwardCommittee) {
                this.awardCommitteeMeetingIdControl.setValidators(Validators.required);
            } else {
                this.awardCommitteeMeetingIdControl.removeValidators(Validators.required);
            }
            this.awardCommitteeMeetingIdControl.updateValueAndValidity({ emitEvent: false });
        }
    }

    private patchValues(form: ApplicationStatusForm | null): void {
        if (form) {
            form.patchValue({
                status: this.status(),
                awardCommitteeMeetingId: this.selectedAwardCommitteeMeeting()?.id || null,
                isMailCommunicationDisabled: this.isMailCommunicationDisabled()
            });
        }
    }
}
